import React from "react";
import { Link } from "react-router-dom";
import { heroImages, species, trips, faqs } from "../data/mock";
import { PageHero, Reveal, SectionTitle, Eyebrow, SpeciesCard, TripCard, CTASection } from "../components/common";
import { Button } from "../components/ui/button";
import { MapPin, Sun, Waves, Fish, CalendarDays } from "lucide-react";
import Seo from "../components/Seo";

const SanJuanDelSur = () => (
  <>
    <Seo
      title="Fishing in San Juan del Sur, Nicaragua | Hog Tide Sport Fishing Charters"
      description="World-class Pacific sport fishing in San Juan del Sur, Nicaragua — sailfish, marlin, yellowfin tuna, roosterfish & more. Private inshore & offshore charters with Hog Tide."
      path="/san-juan-del-sur-fishing"
    />
    <PageHero tall image="https://images.unsplash.com/photo-1569879925512-85ee9bf5c538?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" eyebrow="San Juan del Sur · Nicaragua" title="Fishing in San Juan del Sur, Nicaragua" sub="World-class Pacific sport fishing on Nicaragua's south-west coast — billfish, tuna, roosterfish and more, minutes from the bay." />
    <section className="py-16">
      <div className="container-x grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-5 text-slate-200 leading-relaxed">
          <Eyebrow>The premier Nicaraguan fishing destination</Eyebrow>
          <h2 className="font-display text-4xl text-white">Why San Juan del Sur is a fishing paradise</h2>
          <p>San Juan del Sur sits on one of the most productive stretches of the Central American Pacific. Warm, nutrient-rich water, dramatic coastline and quick access to deep offshore grounds mean anglers can chase sailfish, marlin and giant yellowfin tuna within a short run of the bay — then work the beaches and rocky points for trophy roosterfish on the way home.</p>
          <p>Because the deep water sits so close to shore, a single Hog Tide charter can combine explosive offshore billfish action with hard-hitting inshore fishing. Add uncrowded surf breaks, dolphins, seasonal humpback whales and unforgettable sunsets, and it's easy to see why San Juan del Sur has become the go-to fishing town in Nicaragua.</p>
          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            {[[Sun,"Fishing year-round","Every month offers excellent action — only the target species change."],[Waves,"Deep water close in","Offshore grounds are a short, comfortable run from the bay."],[Fish,"20+ game species","From billfish and tuna to roosterfish, snapper and jacks."],[MapPin,"Easy to reach","~2.5 hrs from Managua airport; close to Costa Rica border."]].map(([Icon,t,d]) => (
              <div key={t} className="rounded-xl bg-[#0e2740] border border-white/10 p-4"><Icon className="h-6 w-6 text-[#f5a524]" /><h3 className="font-heading text-lg text-white mt-2">{t}</h3><p className="text-sm text-slate-400">{d}</p></div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-1">
          <div className="sticky top-24 rounded-2xl border border-white/10 bg-[#0e2740] p-6">
            <h3 className="font-heading text-xl text-white">Best time to fish</h3>
            <ul className="mt-3 space-y-3 text-sm text-slate-200">
              <li className="flex justify-between border-b border-white/5 pb-2"><span>Sailfish</span><span className="text-[#f5a524]">Dec – Apr</span></li>
              <li className="flex justify-between border-b border-white/5 pb-2"><span>Blue & Black Marlin</span><span className="text-[#f5a524]">May – Sep</span></li>
              <li className="flex justify-between border-b border-white/5 pb-2"><span>Yellowfin Tuna</span><span className="text-[#f5a524]">May – Nov</span></li>
              <li className="flex justify-between border-b border-white/5 pb-2"><span>Roosterfish</span><span className="text-[#f5a524]">May – Oct</span></li>
              <li className="flex justify-between"><span>Mahi-Mahi</span><span className="text-[#f5a524]">Year-Round</span></li>
            </ul>
            <Link to="/fishing-calendar"><Button variant="outline" className="w-full mt-4 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white gap-2"><CalendarDays className="h-4 w-4" /> Full season calendar</Button></Link>
            <Link to="/plan-your-trip"><Button className="btn-gold w-full mt-3">Plan Your Trip</Button></Link>
          </div>
        </div>
      </div>
    </section>

    <section className="py-6">
      <div className="container-x">
        <SectionTitle center eyebrow="Target species" title="What you'll catch here" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{species.slice(0,4).map((s,i)=>(<Reveal key={s.slug} delay={i*0.06}><SpeciesCard s={s} /></Reveal>))}</div>
      </div>
    </section>

    <section className="py-16">
      <div className="container-x">
        <SectionTitle center eyebrow="Popular charters" title="San Juan del Sur fishing trips" />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{trips.slice(0,3).map((t,i)=>(<Reveal key={t.slug} delay={i*0.08}><TripCard trip={t} /></Reveal>))}</div>
      </div>
    </section>

    <section className="py-6">
      <div className="container-x max-w-3xl">
        <SectionTitle center eyebrow="Good to know" title="San Juan del Sur fishing FAQ" />
        <div className="space-y-3">
          {faqs.slice(0,4).map((f) => (
            <div key={f.q} className="rounded-xl bg-[#0e2740] border border-white/10 p-5"><h3 className="font-heading text-lg text-white">{f.q}</h3><p className="text-sm text-slate-300 mt-2">{f.a}</p></div>
          ))}
        </div>
      </div>
    </section>
    <CTASection image={heroImages[2].img} />
  </>
);

export default SanJuanDelSur;
